// API contract for /api/admin/provisioning/* — mirrors the backend
// provisioning module DTOs (backend/api/src/modules/provisioning/dto/).
// All field names are snake_case to match the wire format. Until the real
// Provisioning API is deployed, these types are satisfied by the MSW mock
// layer (src/mocks/).

/** RBAC tier of a playbook template. */
export type ProvisioningTier = 'SAFE' | 'DEPLOY' | 'DESTRUCTIVE';

/** Lifecycle status of a provisioning task. */
export type ProvisioningTaskStatus = 'queued' | 'running' | 'success' | 'failed' | 'stopped';

/** Terminal task statuses — no further polling once reached. */
export const TERMINAL_TASK_STATUSES: readonly ProvisioningTaskStatus[] = [
  'success',
  'failed',
  'stopped',
];

export function isTerminalStatus(status: ProvisioningTaskStatus | undefined): boolean {
  return status !== undefined && TERMINAL_TASK_STATUSES.includes(status);
}

/** Survey-field input types we recognise. `password` fields are redacted. */
export type SurveyFieldType = 'text' | 'password';

/** One survey input field, as declared in templates.yml. */
export interface SurveyField {
  name: string;
  title: string;
  type: SurveyFieldType;
  required: boolean;
  default?: string;
  /**
   * Optional enum choices. The backend survey schema is free-form text, but
   * some templates encode choices in the title (e.g. "Action (start/stop)").
   * When present the UI renders a <select> instead of a text input.
   */
  enum?: string[];
}

/** A template summary in GET /playbooks and the full schema in GET /playbooks/:id. */
export interface Playbook {
  id: number;
  name: string;
  group: string;
  tier: ProvisioningTier;
  description: string | null;
  survey: SurveyField[];
}

/** Returned by POST /playbooks/:id/run. */
export interface RunPlaybookResponse {
  task_id: number;
}

/** Embedded template reference inside a task response. */
export interface TaskTemplateRef {
  id: number;
  name: string;
  tier: ProvisioningTier;
}

/** Task status payload for GET /tasks/:task_id and rows of GET /tasks. */
export interface ProvisioningTask {
  task_id: number;
  status: ProvisioningTaskStatus;
  template: TaskTemplateRef;
  actor: string;
  args: Record<string, unknown>;
  reason: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/** A page of task history from GET /tasks. */
export interface PaginatedTasks {
  items: ProvisioningTask[];
  page: number;
  page_size: number;
  total: number;
}

/** A single task-output log line proxied from Semaphore. */
export interface TaskLogLine {
  time: string;
  output: string;
}

/** Body for POST /playbooks/:id/run. */
export interface RunPlaybookBody {
  args: Record<string, unknown>;
  reason?: string;
}

/** Query params for GET /tasks — the task-history audit view. */
export interface TaskHistoryQuery {
  status?: ProvisioningTaskStatus;
  template?: string;
  actor?: string;
  since?: string;
  page?: number;
  page_size?: number;
}
